import { datadogRum } from "@datadog/browser-rum";
import { RiLoopRightLine } from "@remixicon/react";
import dayjs from "dayjs";
import { useAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { identifyHeapUser, trackHeapEvent } from "@/helpers/heap";
import { useGetStructuredServicesByOrganisation } from "@/hooks";
import { useAuth } from "@/hooks/Auth";
import { UserHasNoOrganisationPage } from "@/pages/UserHasNoOrganisationPage";
import { tolgee } from "@/providers/TranslationProvider";
import {
  selectedClientServiceIdAtom,
  selectedOrganisationIdAtom,
  userAtom,
} from "@/store";
import { type AuthenticateGuardProps } from "./AuthenticateGuard.types";

export const AuthenticateGuard = ({ children }: AuthenticateGuardProps) => {
  const [userAtomValue, setUserAtomValue] = useAtom(userAtom);
  const [
    selectedClientServiceIdAtomValue,
    setSelectedClientServiceIdAtomValue,
  ] = useAtom(selectedClientServiceIdAtom);
  const [selectedOrganisationIdAtomValue] = useAtom(selectedOrganisationIdAtom);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { isAuthenticated, useGetUser } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { data: userData, isLoading: isLoadingUserData } = useGetUser();

  const { serviceToSelectByDefault } = useGetStructuredServicesByOrganisation();

  const hasNoService = useMemo<boolean>(
    () => !(userAtomValue?.Services && userAtomValue.Services.length > 0),
    [userAtomValue]
  );

  useEffect(() => {
    if (!isLoadingUserData && userData) {
      setUserAtomValue(userData);

      tolgee
        .changeLanguage(userData?.lang || tolgee.getLanguage())
        .catch((e) => console.error(e));
      dayjs.locale(userData?.lang || tolgee.getLanguage());
    }
  }, [isLoadingUserData, setUserAtomValue, userData]);

  useEffect(() => {
    if (
      !selectedOrganisationIdAtomValue ||
      !selectedClientServiceIdAtomValue ||
      !userData
    ) {
      return;
    }

    identifyHeapUser(userData?.id, { user_role: userData?.role });
    trackHeapEvent("login", {
      client_id: userData?.id,
      user_role: userData?.role,
      service_id: selectedClientServiceIdAtomValue,
      organization_id: selectedOrganisationIdAtomValue,
    });
  }, [
    selectedClientServiceIdAtomValue,
    selectedOrganisationIdAtomValue,
    userData,
  ]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const serviceInQueryParams = queryParams.get("clientServiceId");

    if (serviceInQueryParams) {
      setSelectedClientServiceIdAtomValue(+serviceInQueryParams);
    } else if (
      !selectedClientServiceIdAtomValue &&
      serviceToSelectByDefault &&
      !userData?.Services.map((s) => s.id).includes(
        selectedClientServiceIdAtomValue
      )
    ) {
      setSelectedClientServiceIdAtomValue(serviceToSelectByDefault.id);
    }
  }, [
    userData,
    selectedClientServiceIdAtomValue,
    serviceToSelectByDefault,
    setSelectedClientServiceIdAtomValue,
  ]);

  useEffect(() => {
    if (
      isAuthenticated &&
      !!userData?.id &&
      datadogRum.getUser()?.id !== userData.id
    ) {
      datadogRum.setUser({
        name: `${userData.firstname} ${userData.lastname}`,
        email: userData.email,
        id: userData.id,
      });
    }
  }, [isAuthenticated, userData]);

  useEffect(() => {
    if (isLoadingUserData) {
      return;
    }

    if (!isAuthenticated && !pathname.startsWith("/auth")) {
      setIsRedirecting(true);
      void navigate("/auth/signIn");
    } else if (isAuthenticated && pathname.startsWith("/auth")) {
      setIsRedirecting(true);
      void navigate("/");
    }
  }, [isAuthenticated, isLoadingUserData, navigate, pathname]);

  if (isLoadingUserData || isRedirecting) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <RiLoopRightLine className="block size-si-sm animate-spin" />
      </div>
    );
  }

  if (isAuthenticated && hasNoService) {
    return <UserHasNoOrganisationPage />;
  }

  return children;
};
